import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { useLogin, useRegister, useAuth } from "@/hooks/use-auth";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { User, Building2, Eye, EyeOff, Check, ChevronsUpDown, Loader2, Mail } from "lucide-react";
import beeLogo from "@assets/logo-bee-yellow_1778007378692.png";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

interface VenueLite {
  id: number;
  name: string;
}

// Public provider client IDs, injected at build time. When absent, the social
// buttons fall back to the full-page /api/auth/* redirect links so nothing
// breaks in environments that haven't configured these vars.
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
const APPLE_CLIENT_ID = import.meta.env.VITE_APPLE_CLIENT_ID as string | undefined;

// Load an external script once (idempotent by id). Resolves when ready.
function loadScript(src: string, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.id = id;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

function StepIndicator({ step }: { step: 1 | 2 }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-6">
      <div className="flex items-center gap-2">
        <div className={cn(
          "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors",
          step >= 1
            ? "bg-primary border-primary text-primary-foreground"
            : "border-zinc-600 text-zinc-600"
        )}>
          {step > 1 ? <Check className="w-4 h-4" /> : "1"}
        </div>
        <span className={cn("text-xs font-medium", step >= 1 ? "text-primary" : "text-zinc-600")}>
          Your details
        </span>
      </div>
      <div className={cn("w-8 h-px mx-2", step >= 2 ? "bg-primary" : "bg-zinc-700")} />
      <div className="flex items-center gap-2">
        <div className={cn(
          "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors",
          step >= 2
            ? "bg-primary border-primary text-primary-foreground"
            : "border-zinc-600 text-zinc-600"
        )}>
          2
        </div>
        <span className={cn("text-xs font-medium", step >= 2 ? "text-primary" : "text-zinc-600")}>
          Account type
        </span>
      </div>
    </div>
  );
}

export default function AuthPage() {
  const [activeTab, setActiveTab] = useState<"signin" | "signup">("signin");
  const [signupStep, setSignupStep] = useState<1 | 2>(1);

  // Sign in fields
  const [loginUsername, setLoginUsername] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Magic link fields
  const [magicEmail, setMagicEmail] = useState("");
  const [magicSending, setMagicSending] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [magicError, setMagicError] = useState("");

  // Sign up step 1 fields
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [gender, setGender] = useState("");
  const [dobError, setDobError] = useState("");

  // Sign up step 2 fields
  const [accountType, setAccountType] = useState<"user" | "business">("user");
  const [selectedVenueId, setSelectedVenueId] = useState<number | null>(null);
  const [selectedVenueName, setSelectedVenueName] = useState("");
  const [venueSearch, setVenueSearch] = useState("");
  const [venueOpen, setVenueOpen] = useState(false);
  const [applicantRole, setApplicantRole] = useState("");
  const [businessEmail, setBusinessEmail] = useState("");
  const [companiesHouseNumber, setCompaniesHouseNumber] = useState("");

  const [submitError, setSubmitError] = useState("");
  const [isPending, setIsPending] = useState(false);

  const preventRedirect = useRef(false);
  const googleBtnRef = useRef<HTMLDivElement>(null);

  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const login = useLogin();
  const register = useRegister();
  const { user } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    if (error === "google_failed") {
      setSubmitError("Google sign-in failed. Please try again.");
    } else if (error === "apple_failed") {
      setSubmitError("Apple sign-in failed. Please try again.");
    } else if (error === "invalid_link") {
      setSubmitError("This login link has expired or already been used. Please request a new one.");
    }
  }, []);

  // Route the user after a successful in-page social sign-in. Onboarding-
  // incomplete users go to onboarding; everyone else lands on the map (the
  // AuthGuard re-routes business/admin roles). Mirrors the server redirect
  // routes so both sign-in mechanisms behave identically.
  async function finishSocialSignIn(onboardingComplete: boolean) {
    await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
    preventRedirect.current = true;
    setLocation(onboardingComplete ? "/" : "/onboarding");
  }

  // Google Identity Services: the credential (an ID token) is returned to this
  // callback in-page, then POSTed same-origin so the session cookie lands in
  // the standalone PWA's cookie jar.
  async function handleGoogleCredential(response: { credential?: string }) {
    if (!response?.credential) return;
    setSubmitError("");
    try {
      const res = await apiRequest("POST", "/api/auth/google/token", {
        credential: response.credential,
      });
      const data = await res.json();
      await finishSocialSignIn(!!data.onboardingComplete);
    } catch {
      setSubmitError("Google sign-in failed. Please try again.");
    }
  }

  // Sign in with Apple JS in popup mode: the promise resolves with the id_token
  // (and, on first sign-in only, the user's name) without leaving the page.
  async function handleAppleSignIn() {
    const AppleID = (window as any).AppleID;
    if (!AppleID?.auth) {
      setSubmitError("Apple sign-in failed. Please try again.");
      return;
    }
    setSubmitError("");
    try {
      const data = await AppleID.auth.signIn();
      const res = await apiRequest("POST", "/api/auth/apple/token", {
        id_token: data?.authorization?.id_token,
        user: data?.user,
      });
      const result = await res.json();
      await finishSocialSignIn(!!result.onboardingComplete);
    } catch (err: any) {
      // User dismissing the Apple popup is not an error worth surfacing.
      if (err?.error === "popup_closed_by_user" || err?.error === "user_cancelled_authorize") {
        return;
      }
      setSubmitError("Apple sign-in failed. Please try again.");
    }
  }

  // Load + initialise Google Identity Services and render its button.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;
    loadScript("https://accounts.google.com/gsi/client", "google-gsi")
      .then(() => {
        if (cancelled) return;
        const google = (window as any).google;
        if (!google?.accounts?.id || !googleBtnRef.current) return;
        google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCredential,
          ux_mode: "popup",
        });
        const width = Math.min(googleBtnRef.current.clientWidth || 320, 400);
        google.accounts.id.renderButton(googleBtnRef.current, {
          theme: "filled_black",
          size: "large",
          shape: "pill",
          text: "continue_with",
          logo_alignment: "center",
          width,
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load + initialise Sign in with Apple JS in popup mode.
  useEffect(() => {
    if (!APPLE_CLIENT_ID) return;
    let cancelled = false;
    loadScript(
      "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js",
      "apple-auth",
    )
      .then(() => {
        if (cancelled) return;
        const AppleID = (window as any).AppleID;
        if (!AppleID?.auth) return;
        AppleID.auth.init({
          clientId: APPLE_CLIENT_ID,
          scope: "name email",
          redirectURI: window.location.origin + "/api/auth/apple/callback",
          usePopup: true,
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: venuesData } = useQuery<{ venues: VenueLite[] }>({
    queryKey: ["/api/venues/lite"],
    queryFn: async () => {
      const res = await fetch("/api/venues/lite", { credentials: "include" });
      return await res.json();
    },
    enabled: signupStep === 2 && accountType === "business",
  });

  const venues = venuesData?.venues ?? [];
  const filteredVenues = venueSearch.trim()
    ? venues.filter(v => v.name.toLowerCase().includes(venueSearch.toLowerCase()))
    : venues;

  useEffect(() => {
    if (user && !preventRedirect.current) {
      if (user.role === "admin") setLocation("/admin");
      else if (user.role === "business") setLocation("/business");
      else setLocation("/");
    }
  }, [user, setLocation]);

  if (user && !isPending) return null;

  function validateAge(dob: string): boolean {
    if (!dob) return false;
    const birth = new Date(dob);
    const today = new Date();
    const age = today.getFullYear() - birth.getFullYear() -
      (today < new Date(today.getFullYear(), birth.getMonth(), birth.getDate()) ? 1 : 0);
    return age >= 18;
  }

  function handleDobBlur() {
    if (dateOfBirth && !validateAge(dateOfBirth)) {
      setDobError("You must be 18 or older to sign up");
    } else {
      setDobError("");
    }
  }

  const step1Valid =
    username.trim().length >= 3 &&
    email.trim().length > 0 &&
    password.length >= 6 &&
    dateOfBirth.length > 0 &&
    validateAge(dateOfBirth) &&
    gender.length > 0;

  const businessFormValid =
    accountType === "business"
      ? selectedVenueId !== null && applicantRole.trim().length > 0
      : true;

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError("");
    try {
      const result = await login.mutateAsync({ username: loginUsername, password: loginPassword });
      if (result.role === "admin") setLocation("/admin");
      else if (result.role === "business") setLocation("/business");
      else setLocation("/");
    } catch (err: any) {
      const msg = err.message || "Sign in failed";
      setSubmitError(msg.includes(":") ? msg.split(": ").slice(1).join(": ") : msg);
    }
  }

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError("");
    setIsPending(true);
    preventRedirect.current = true;
    try {
      await register.mutateAsync({ username, email, password, dateOfBirth, gender, accountType: "user" });

      if (accountType === "business") {
        try {
          await apiRequest("POST", "/api/business/apply", {
            venueId: selectedVenueId,
            applicantRole,
            businessEmail: businessEmail || undefined,
            companiesHouseNumber: companiesHouseNumber || undefined,
          });
          preventRedirect.current = false;
          setLocation("/");
          toast({
            title: "Application submitted!",
            description: "We'll review your application and notify you by email and in-app once a decision has been made.",
          });
        } catch (applyErr: any) {
          const msg = applyErr.message || "Failed to submit application";
          setSubmitError(msg.includes(":") ? msg.split(": ").slice(1).join(": ") : msg);
          queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
        }
      } else {
        preventRedirect.current = false;
        setLocation("/");
      }
    } catch (err: any) {
      const msg = err.message || "Registration failed";
      setSubmitError(msg.includes(":") ? msg.split(": ").slice(1).join(": ") : msg);
    } finally {
      preventRedirect.current = false;
      setIsPending(false);
    }
  }

  const inputCls = "bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-600 h-11 rounded-xl";
  const labelCls = "text-zinc-400 text-sm mb-1 block";

  return (
    <div className="h-[100dvh] overflow-y-auto scrollbar-hide bg-background flex flex-col items-center px-6 pb-8 pt-[max(2rem,calc(env(safe-area-inset-top)+1rem))]">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-3">
            <img src={beeLogo} alt="Buzzn bee logo" className="w-8 h-8 object-contain" data-testid="img-buzzn-logo" />
            <h1 className="text-3xl font-display text-white tracking-tight font-black">Buzzn</h1>
          </div>
          <p className="text-sm text-[#ffffff]">Whatever your buzz is, you'll find it here.</p>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v as "signin" | "signup"); setSubmitError(""); setSignupStep(1); }}>
          <TabsList className="w-full bg-zinc-900 border border-zinc-800 mb-6 h-11 rounded-xl">
            <TabsTrigger
              data-testid="tab-signin"
              value="signin"
              className="flex-1 rounded-lg data-[state=active]:bg-zinc-800 data-[state=active]:text-white text-zinc-500"
            >
              Sign in
            </TabsTrigger>
            <TabsTrigger
              data-testid="tab-signup"
              value="signup"
              className="flex-1 rounded-lg data-[state=active]:bg-zinc-800 data-[state=active]:text-white text-zinc-500"
            >
              Sign up
            </TabsTrigger>
          </TabsList>

          {/* ── SIGN IN ── */}
          <TabsContent value="signin">
            <form onSubmit={handleSignIn} className="space-y-4">
              <div>
                <Label className={labelCls}>Username</Label>
                <Input
                  data-testid="input-login-username"
                  type="text"
                  placeholder="Username"
                  value={loginUsername}
                  onChange={(e) => setLoginUsername(e.target.value)}
                  className={inputCls}
                  required
                />
              </div>
              <div>
                <Label className={labelCls}>Password</Label>
                <Input
                  data-testid="input-login-password"
                  type="password"
                  placeholder="Password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className={inputCls}
                  required
                />
              </div>
              {submitError && <p className="text-red-400 text-sm">{submitError}</p>}
              <Button
                data-testid="button-signin"
                type="submit"
                className="w-full h-11 rounded-xl bg-primary hover:bg-primary text-primary-foreground font-bold"
                disabled={login.isPending}
              >
                {login.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
              </Button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-zinc-800" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-background px-3 text-zinc-500">or</span>
                </div>
              </div>

              {GOOGLE_CLIENT_ID ? (
                <div
                  ref={googleBtnRef}
                  data-testid="button-google-signin"
                  className="w-full flex justify-center min-h-[44px]"
                />
              ) : (
                <a
                  href="/api/auth/google"
                  data-testid="button-google-signin"
                  className="flex items-center justify-center gap-3 w-full h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white font-semibold text-sm transition-colors"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                    <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 2.58 9 3.58z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </a>
              )}

              {APPLE_CLIENT_ID ? (
                <button
                  type="button"
                  onClick={handleAppleSignIn}
                  data-testid="button-apple-signin"
                  className="flex items-center justify-center gap-3 w-full h-11 rounded-xl bg-black hover:bg-zinc-900 text-white font-semibold text-sm transition-colors border border-zinc-700"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.71 9.27c-.02-2.01 1.64-2.98 1.71-3.02-0.93-1.36-2.38-1.55-2.9-1.57-1.23-.13-2.41.73-3.04.73-.63 0-1.6-.71-2.63-.69-1.35.02-2.6.79-3.3 2.01-1.41 2.44-.36 6.06 1.01 8.04.67.97 1.47 2.06 2.52 2.02 1.01-.04 1.39-.65 2.61-.65 1.22 0 1.56.65 2.62.63 1.09-.02 1.78-.99 2.44-1.96.77-1.12 1.09-2.21 1.1-2.27-.02-.01-2.12-.81-2.14-3.23zM11.73 3.47c.56-.68.93-1.62.83-2.56-.8.03-1.77.53-2.35 1.21-.51.59-.96 1.54-.84 2.45.89.07 1.8-.45 2.36-1.1z" fill="white"/>
                  </svg>
                  Continue with Apple
                </button>
              ) : (
                <a
                  href="/api/auth/apple"
                  data-testid="button-apple-signin"
                  className="flex items-center justify-center gap-3 w-full h-11 rounded-xl bg-black hover:bg-zinc-900 text-white font-semibold text-sm transition-colors border border-zinc-700"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.71 9.27c-.02-2.01 1.64-2.98 1.71-3.02-0.93-1.36-2.38-1.55-2.9-1.57-1.23-.13-2.41.73-3.04.73-.63 0-1.6-.71-2.63-.69-1.35.02-2.6.79-3.3 2.01-1.41 2.44-.36 6.06 1.01 8.04.67.97 1.47 2.06 2.52 2.02 1.01-.04 1.39-.65 2.61-.65 1.22 0 1.56.65 2.62.63 1.09-.02 1.78-.99 2.44-1.96.77-1.12 1.09-2.21 1.1-2.27-.02-.01-2.12-.81-2.14-3.23zM11.73 3.47c.56-.68.93-1.62.83-2.56-.8.03-1.77.53-2.35 1.21-.51.59-.96 1.54-.84 2.45.89.07 1.8-.45 2.36-1.1z" fill="white"/>
                  </svg>
                  Continue with Apple
                </a>
              )}

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-zinc-800" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-background px-3 text-zinc-500">or</span>
                </div>
              </div>

              {magicSent ? (
                <div className="text-center space-y-3 py-2">
                  <Mail className="w-10 h-10 text-primary mx-auto" />
                  <p className="text-white font-semibold text-base">Check your inbox</p>
                  <p className="text-zinc-400 text-sm">
                    We sent a login link to <span className="text-primary">{magicEmail}</span>. It expires in 30 minutes.
                  </p>
                  <button
                    data-testid="link-magic-reset"
                    type="button"
                    onClick={() => { setMagicSent(false); setMagicEmail(""); setMagicError(""); }}
                    className="text-zinc-500 hover:text-zinc-400 text-xs underline"
                  >
                    Wrong email? Go back
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Input
                    data-testid="input-magic-email"
                    type="email"
                    placeholder="Enter your email address"
                    value={magicEmail}
                    onChange={(e) => { setMagicEmail(e.target.value); setMagicError(""); }}
                    className={inputCls}
                  />
                  {magicError && <p className="text-red-400 text-xs">{magicError}</p>}
                  <Button
                    data-testid="button-magic-send"
                    type="button"
                    disabled={magicSending || !magicEmail.trim()}
                    onClick={async () => {
                      setMagicError("");
                      setMagicSending(true);
                      try {
                        const res = await fetch("/api/auth/magic-link/request", {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ email: magicEmail }),
                          credentials: "include",
                        });
                        if (!res.ok) {
                          const data = await res.json();
                          throw new Error(data.message || "Failed to send");
                        }
                        setMagicSent(true);
                      } catch (err: any) {
                        setMagicError(err.message || "Failed to send email, please try again");
                      } finally {
                        setMagicSending(false);
                      }
                    }}
                    className="w-full h-11 rounded-xl bg-primary hover:bg-primary text-primary-foreground font-bold disabled:opacity-40"
                  >
                    {magicSending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send login link"}
                  </Button>
                </div>
              )}

              <p className="text-center text-zinc-500 text-sm mt-4">
                Don't have an account?{" "}
                <button
                  data-testid="link-goto-signup"
                  type="button"
                  onClick={() => setActiveTab("signup")}
                  className="text-primary hover:text-primary underline"
                >
                  Sign up
                </button>
              </p>
            </form>
          </TabsContent>

          {/* ── SIGN UP ── */}
          <TabsContent value="signup">
            <StepIndicator step={signupStep} />

            {signupStep === 1 && (
              <div className="space-y-4">
                <div>
                  <Label className={labelCls}>Username</Label>
                  <Input
                    data-testid="input-signup-username"
                    type="text"
                    placeholder="Choose a username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className={inputCls}
                    minLength={3}
                  />
                  {username.length > 0 && username.length < 3 && (
                    <p className="text-red-400 text-xs mt-1">At least 3 characters</p>
                  )}
                </div>
                <div>
                  <Label className={labelCls}>Email</Label>
                  <Input
                    data-testid="input-signup-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={inputCls}
                  />
                </div>
                <div>
                  <Label className={labelCls}>Password</Label>
                  <div className="relative">
                    <Input
                      data-testid="input-signup-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Min. 6 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={cn(inputCls, "pr-10")}
                      minLength={6}
                    />
                    <button
                      data-testid="button-toggle-password"
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {password.length > 0 && password.length < 6 && (
                    <p className="text-red-400 text-xs mt-1">At least 6 characters</p>
                  )}
                </div>
                <div>
                  <Label className={labelCls}>Date of birth</Label>
                  <Input
                    data-testid="input-signup-dob"
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => { setDateOfBirth(e.target.value); setDobError(""); }}
                    onBlur={handleDobBlur}
                    className={cn(inputCls, "[color-scheme:dark]")}
                    max={new Date().toISOString().split("T")[0]}
                  />
                  {dobError && <p className="text-red-400 text-xs mt-1">{dobError}</p>}
                </div>
                <div>
                  <Label className={labelCls}>Gender</Label>
                  <Select value={gender} onValueChange={setGender}>
                    <SelectTrigger
                      data-testid="select-signup-gender"
                      className={cn(inputCls, "text-left")}
                    >
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                      <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="non_binary">Non-binary</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  data-testid="button-continue"
                  type="button"
                  onClick={() => setSignupStep(2)}
                  disabled={!step1Valid}
                  className="w-full h-11 rounded-xl bg-primary hover:bg-primary text-primary-foreground font-bold disabled:opacity-40"
                >
                  Continue
                </Button>
              </div>
            )}

            {signupStep === 2 && (
              <form onSubmit={handleSignUp} className="space-y-4">
                <button
                  data-testid="link-back-to-step1"
                  type="button"
                  onClick={() => setSignupStep(1)}
                  className="text-zinc-400 hover:text-zinc-200 text-sm underline"
                >
                  ← Back
                </button>

                {/* Account type cards */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    data-testid="card-account-explorer"
                    type="button"
                    onClick={() => setAccountType("user")}
                    className={cn(
                      "flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-colors bg-zinc-900 text-white",
                      accountType === "user"
                        ? "border-primary"
                        : "border-zinc-700 hover:border-zinc-500"
                    )}
                  >
                    <User className="w-8 h-8 text-primary" />
                    <span className="font-semibold text-sm">Explorer</span>
                    <span className="text-[11px] text-zinc-400 text-center leading-tight">
                      Discover venues, report vibes and earn Honey
                    </span>
                  </button>
                  <button
                    data-testid="card-account-business"
                    type="button"
                    onClick={() => setAccountType("business")}
                    className={cn(
                      "flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-colors bg-zinc-900 text-white",
                      accountType === "business"
                        ? "border-primary"
                        : "border-zinc-700 hover:border-zinc-500"
                    )}
                  >
                    <Building2 className="w-8 h-8 text-primary" />
                    <span className="font-semibold text-sm">Business</span>
                    <span className="text-[11px] text-zinc-400 text-center leading-tight">
                      Manage your venue and create Honey specials
                    </span>
                  </button>
                </div>

                {/* Business-only fields */}
                {accountType === "business" && (
                  <div className="space-y-4">
                    <div>
                      <Label className={labelCls}>Your venue</Label>
                      <Popover open={venueOpen} onOpenChange={setVenueOpen}>
                        <PopoverTrigger asChild>
                          <button
                            data-testid="combobox-venue-trigger"
                            type="button"
                            role="combobox"
                            aria-expanded={venueOpen}
                            className={cn(
                              "w-full flex items-center justify-between h-11 px-3 rounded-xl border border-zinc-800 bg-zinc-900 text-sm text-white hover:border-zinc-700 transition-colors",
                              !selectedVenueName && "text-zinc-600"
                            )}
                          >
                            {selectedVenueName || "Search for your venue..."}
                            <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0 ml-2" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0 bg-zinc-900 border-zinc-800" align="start">
                          <Command className="bg-zinc-900">
                            <CommandInput
                              data-testid="input-venue-search"
                              placeholder="Search for your venue..."
                              value={venueSearch}
                              onValueChange={setVenueSearch}
                              className="text-white placeholder:text-zinc-500"
                            />
                            <CommandList className="max-h-52">
                              <CommandEmpty className="text-zinc-500 py-4 text-center text-sm">No venues found.</CommandEmpty>
                              <CommandGroup>
                                {filteredVenues.slice(0, 50).map((venue) => (
                                  <CommandItem
                                    data-testid={`venue-option-${venue.id}`}
                                    key={venue.id}
                                    value={venue.name}
                                    onSelect={() => {
                                      setSelectedVenueId(venue.id);
                                      setSelectedVenueName(venue.name);
                                      setVenueSearch("");
                                      setVenueOpen(false);
                                    }}
                                    className="text-white data-[selected=true]:bg-zinc-800 cursor-pointer"
                                  >
                                    <Check className={cn("w-4 h-4 mr-2 text-primary", selectedVenueId === venue.id ? "opacity-100" : "opacity-0")} />
                                    {venue.name}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label className={labelCls}>Your role at this venue</Label>
                      <Input
                        data-testid="input-applicant-role"
                        type="text"
                        placeholder="e.g. Owner, Manager, Director"
                        value={applicantRole}
                        onChange={(e) => setApplicantRole(e.target.value)}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <Label className={labelCls}>Business email <span className="text-zinc-600">(optional)</span></Label>
                      <Input
                        data-testid="input-business-email"
                        type="email"
                        placeholder="hello@yourvenue.co.uk"
                        value={businessEmail}
                        onChange={(e) => setBusinessEmail(e.target.value)}
                        className={inputCls}
                      />
                      <p className="text-zinc-600 text-xs mt-1">Using your venue's domain email strengthens your application</p>
                    </div>
                    <div>
                      <Label className={labelCls}>Companies House number <span className="text-zinc-600">(optional)</span></Label>
                      <Input
                        data-testid="input-companies-house"
                        type="text"
                        placeholder="e.g. 12345678"
                        value={companiesHouseNumber}
                        onChange={(e) => setCompaniesHouseNumber(e.target.value)}
                        className={inputCls}
                      />
                      <p className="text-zinc-600 text-xs mt-1">Find yours at companieshouse.gov.uk — adding this strengthens your application</p>
                    </div>
                    <div className="bg-primary/10 border-l-4 border-primary rounded-r-xl px-4 py-3">
                      <p className="text-primary/80 text-xs leading-relaxed">
                        Business accounts are reviewed by our team before access is granted. You'll have standard user access in the meantime and will be notified by email and in-app once reviewed.
                      </p>
                    </div>
                  </div>
                )}

                {submitError && <p className="text-red-400 text-sm">{submitError}</p>}

                {accountType === "user" ? (
                  <Button
                    data-testid="button-create-account"
                    type="submit"
                    disabled={isPending}
                    className="w-full h-11 rounded-xl bg-primary hover:bg-primary text-primary-foreground font-bold disabled:opacity-40"
                  >
                    {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
                  </Button>
                ) : (
                  <Button
                    data-testid="button-submit-application"
                    type="submit"
                    disabled={isPending || !businessFormValid}
                    className="w-full h-11 rounded-xl bg-primary hover:bg-primary text-primary-foreground font-bold disabled:opacity-40"
                  >
                    {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit application"}
                  </Button>
                )}
              </form>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
